import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import { type DrawerProps } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { businessSelectors, businessTypes } from 'app-components/flux/business';
import { customerFormFactory } from 'app-components/flux/customers/form/customerFormModel';
import { createFormHooks } from 'app-components/utils/createForm/formHooks';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import { getDrawerSx } from 'components/Drawers/drawerStyles';
import QButton, { QButtonVariantEnum } from 'components/QButton';
import TooltipSubmitButton from 'components/QButton/TooltipSubmitButton';
import UsageAndEntityIcon from 'components/UsageAndEntityIcon';
import UsageSelect from 'components/UsageSelect';
import useCustomerActions from 'containers/Customers/hooks/useCustomersActions';
import { useAppSelector } from 'hooks/redux';
import React, { useSyncExternalStore } from 'react';
import { makeStyles } from 'tss-react/mui';

export const { FormProvider, ...customerFormHooks } = createFormHooks(customerFormFactory);

const useStyles = makeStyles()((theme) => ({
  addressFieldRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  addressFullField: {
    width: '100%',
  },
  addressCityField: {
    flex: '5 1 0',
    minWidth: 0,
  },
  addressStateField: {
    flex: '2 1 0',
    minWidth: 0,
  },
  addressZipField: {
    flex: '3 1 0',
    minWidth: 0,
  },
  instructionText: {
    marginTop: 24,
  },
  generalFieldRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  generalHalfField: {
    flex: '1 1 0',
    minWidth: 0,
  },
  generalFullField: {
    width: '100%',
  },
  dividerMargin: {
    marginTop: 24,
  },
  errorTab: {
    minHeight: `48px !important`,
    color: `${theme.palette.error.main} !important`,
  },
  tabContainer: {
    borderBottom: 1,
    borderColor: theme.palette.divider,
  },
  errorIcon: {
    color: theme.palette.error.main,
    fontSize: '20px',
  },
  ellipsizeOverflow: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

function CustomerTextField({
  id,
  fieldName,
  className,
  children,
  ...rest
}: {
  fieldName:
    | 'firstName'
    | 'lastName'
    | 'businessName'
    | 'email'
    | 'phone'
    | 'note'
    | 'line1'
    | 'line2'
    | 'zipCode'
    | 'city'
    | 'state';
} & TextFieldProps) {
  const { onValueChange, value, helperText, error } = customerFormHooks.useField(fieldName);
  return (
    <TextField
      id={id}
      data-testid={id}
      {...rest}
      value={value}
      helperText={helperText}
      error={error}
      onChange={(e) => onValueChange(e.target.value)}
      autoComplete='off'
      className={className}
    >
      {children}
    </TextField>
  );
}

export const BusinessSelect = React.memo(function BusinessSelect() {
  const { value, onValueChange, error, helperText, hidden } = customerFormHooks.useField('businessId');
  const { classes } = useStyles();

  const businesses = useAppSelector(businessSelectors.getResourcesById);
  const business = businesses?.get(value ?? '') ?? null;

  if (hidden) {
    return null;
  }

  return (
    <>
      <UsageSelect
        value={value}
        onChange={(_event, newValue) => {
          onValueChange(newValue);
        }}
        showPersonal={false}
        iconOnly={false}
        nameAndIcon={true}
        sx={{
          width: '100%',
          height: '100%',
          marginBottom: 0,
          marginTop: 3,
        }}
        id='usage-select'
        error={error}
        helperText={helperText}
        hiddenLabel={false}
        label='Business'
        slotProps={{
          select: {
            renderValue: () => (
              <Stack direction='row' alignItems='center' sx={{ pt: 1 }}>
                <IconButton sx={{ pr: 2 }}>
                  <UsageAndEntityIcon color={business?.color} size='small' usageIcon={'BUSINESS'} />
                </IconButton>
                <Typography className={classes.ellipsizeOverflow}>{business?.name ?? ''}</Typography>
              </Stack>
            ),
          },
          inputLabel: {
            shrink: !!value,
          },
        }}
      />
      <Divider className={classes.dividerMargin} />
    </>
  );
});

export const GeneralSection = React.memo(function GeneralSection() {
  const { classes } = useStyles();
  return (
    <Box>
      <Typography variant='paragraphRegular' color='textSecondary' className={classes.instructionText}>
        Enter a company and/or contact name
      </Typography>
      <Box className={classes.generalFieldRow}>
        <CustomerTextField
          label='First Name'
          margin='normal'
          fieldName='firstName'
          className={classes.generalHalfField}
          id='first-name-field'
          autoFocus
        />
        <CustomerTextField
          label='Last Name'
          margin='normal'
          fieldName={'lastName'}
          className={classes.generalHalfField}
          id='last-name-field'
        />
      </Box>
      <CustomerTextField
        label='Company Name'
        margin='normal'
        fieldName='businessName'
        className={classes.generalFullField}
        id='company-name-field'
      />
      <Divider className={classes.dividerMargin} />
      <BusinessSelect />
      <Typography variant='paragraphRegular' color='textSecondary' className={classes.instructionText}>
        Contact details (optional)
      </Typography>
      <Box className={classes.generalFieldRow}>
        <CustomerTextField
          label='Email'
          margin='normal'
          fieldName='email'
          className={classes.generalHalfField}
          id='email-field'
        />
        <CustomerTextField
          label='Phone Number'
          margin='normal'
          fieldName='phone'
          className={classes.generalHalfField}
          id='phone-number-field'
        />
      </Box>
      <CustomerTextField
        label='Private Notes'
        margin='normal'
        fieldName='note'
        id='private-notes-field'
        multiline
        minRows={2}
        className={classes.generalFullField}
      />
    </Box>
  );
});

export const AddressSection = React.memo(function AddressSection() {
  const { classes } = useStyles();
  const { value: addressEnabled, onValueChange } = customerFormHooks.useField('addressEnabled');

  return (
    <Box>
      <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'flex-start'} pt={1}>
        <Switch checked={addressEnabled} onChange={() => onValueChange(!addressEnabled)} />
        <Typography>Mailing address (optional)</Typography>
      </Box>

      <CustomerTextField
        id='street-address-field'
        label='Street Address'
        fieldName='line1'
        className={classes.addressFullField}
        margin='normal'
        variant='filled'
        disabled={!addressEnabled}
      />
      <CustomerTextField
        id='street-address-line2-field'
        label='Address Line 2'
        fieldName='line2'
        className={classes.addressFullField}
        margin='normal'
        variant='filled'
        disabled={!addressEnabled}
      />
      <Box className={classes.addressFieldRow}>
        <CustomerTextField
          id='city-field'
          label='City'
          className={classes.addressCityField}
          margin='normal'
          variant='filled'
          fieldName='city'
          disabled={!addressEnabled}
        />
        <CustomerTextField
          id='state-field'
          label='State'
          className={classes.addressStateField}
          margin='normal'
          variant='filled'
          fieldName='state'
          disabled={!addressEnabled}
          select
        >
          {businessTypes.usStatesArray.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </CustomerTextField>
        <CustomerTextField
          id='zip-field'
          label='ZIP Code'
          fieldName='zipCode'
          className={classes.addressZipField}
          disabled={!addressEnabled}
          margin='normal'
          variant='filled'
        />
      </Box>
    </Box>
  );
});

const isGeneralError = (manager: ReturnType<typeof customerFormHooks.useManager>) =>
  manager.getFieldError('firstName') ||
  manager.getFieldError('lastName') ||
  manager.getFieldError('businessName') ||
  manager.getFieldError('email') ||
  manager.getFieldError('businessId');

const useGeneralError = () => {
  const manager = customerFormHooks.useManager();
  return useSyncExternalStore(manager.subscribe, () => {
    return isGeneralError(manager);
  });
};

export const FormTabs = React.memo(function FormTabs({
  tab,
  setTab,
}: {
  tab: 0 | 1;
  setTab: React.Dispatch<React.SetStateAction<0 | 1>>;
}) {
  const { classes } = useStyles();

  const handleChange = (_e: React.SyntheticEvent, newValue: 0 | 1) => {
    setTab(newValue);
  };

  const { error: addressError } = customerFormHooks.useField('addressEnabled');
  const generalError = useGeneralError();

  const errorIndicator = (generalError && tab === 0) || (addressError && tab === 1);

  return (
    <>
      <Box className={classes.tabContainer}>
        <Tabs
          value={tab}
          onChange={handleChange}
          aria-label='general and mailing address'
          TabIndicatorProps={{
            sx: {
              backgroundColor: errorIndicator ? 'error.main' : undefined,
            },
          }}
        >
          <Tab
            iconPosition={'end'}
            icon={generalError ? <ErrorIcon className={classes.errorIcon} /> : undefined}
            label='General'
            id='general-tab'
            className={generalError ? classes.errorTab : undefined}
          />
          <Tab
            iconPosition={'end'}
            icon={addressError ? <ErrorIcon className={classes.errorIcon} /> : undefined}
            label='Mailing Address'
            id='mailing-address-tab'
            className={addressError ? classes.errorTab : undefined}
          />
        </Tabs>
      </Box>
      {tab === 0 && <GeneralSection />}
      {tab === 1 && <AddressSection />}
    </>
  );
});

export const SubmitButton = React.memo(function SubmitButton({
  setTab,
}: {
  setTab: React.Dispatch<React.SetStateAction<0 | 1>>;
}) {
  const showCreate = customerFormHooks.useSelector((s) => !s.state.customer.id);
  const manager = customerFormHooks.useManager();
  const valid = customerFormHooks.useIsFormValid();
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    await manager.submit();
    const isValid = manager.isFormValid();
    // focus tab on submit if error
    if (!isValid) {
      setTab(isGeneralError(manager) ? 0 : 1);
      setSubmitting(false);
    }
  };

  return (
    <TooltipSubmitButton
      showTooltip={!valid}
      variant='contained'
      onClick={onSubmit}
      disabled={submitting}
      id='submit-button'
    >
      {showCreate ? 'Create' : 'Save'}
    </TooltipSubmitButton>
  );
});

export const ArchiveClient = React.memo(function ArchiveClient() {
  const customerId = customerFormHooks.useSelector((s) => s.state.customer.id);
  const { archiveCustomer } = useCustomerActions();
  if (!customerId) {
    return null;
  }
  return (
    <QButton
      variant={QButtonVariantEnum.DELETE}
      onClick={() => {
        archiveCustomer(customerId, true);
      }}
      id='cancel-button'
      style={{ borderWidth: 0 }}
    >
      Archive Client
    </QButton>
  );
});

const InnerDrawer = function ({ onClose, sx, ...other }: DrawerProps & { onClose: () => void }) {
  const [tab, setTab] = React.useState<0 | 1>(0);
  const isCreate = customerFormHooks.useSelector((s) => !s.state.customer.id);
  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={onClose}
      PaperProps={{
        sx: { width: 620 },
      }}
      {...other}
      sx={getDrawerSx(sx)}
    >
      <StdDialogTitle variant='drawer' onClose={onClose} showCloseButton={true} showBackButton={false}>
        {`${!isCreate ? 'Edit' : 'Add'} Client`}
      </StdDialogTitle>

      <DialogContent>
        <FormTabs tab={tab} setTab={setTab} />
      </DialogContent>

      <DialogActions sx={{ justifyContent: isCreate ? 'space-between' : 'flex-end' }}>
        <ArchiveClient />
        <Stack display='flex' alignItems='center' flexDirection={'row'} gap={1} ml='auto !important'>
          <QButton onClick={onClose} id='cancel-button'>
            Cancel
          </QButton>
          <SubmitButton setTab={setTab} />
        </Stack>
      </DialogActions>
    </Drawer>
  );
};

export default InnerDrawer;
